import { useEffect, useState } from "react";
import { Pulse, ArrowClockwise, PaperPlaneTilt, CheckCircle, XCircle, Warning, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../../lib/api";

const COLORS = { green: "#00D4AA", yellow: "#FFD24C", red: "#FF4D4D" };
const WORDS = { green: "Healthy", yellow: "Needs attention", red: "Broken" };

function browserChecks(apiId) {
  if (typeof window === "undefined") return [];
  const img = document.querySelector('img[src*="facebook.com/tr"]');
  const ns = Array.from(document.querySelectorAll("noscript")).map((n) => n.textContent || "").find((t) => t.includes("facebook.com/tr"));
  const domId = ((img ? img.getAttribute("src") : ns || "").match(/id=(\d+)/) || [])[1] || null;
  const runtimeId = window.__fpai_meta_pixel || null;
  const out = [];
  out.push({ key: "dom_match", ok: domId ? domId === apiId : null, level: "error",
    label: domId ? "Page HTML fires the same pixel the server reports" : "No pixel tag found in page HTML (ad blocker?)", detail: domId });
  out.push({ key: "runtime_match", ok: runtimeId ? runtimeId === apiId : null, level: runtimeId ? "error" : "info",
    label: runtimeId ? "Browser pixel initialised with the same ID" : "Browser pixel not initialised in this tab (ad blocker or not loaded yet)", detail: runtimeId });
  out.push({ key: "fbq", ok: typeof window.fbq === "function" ? true : null, level: "info",
    label: typeof window.fbq === "function" ? "Meta pixel script loaded in this browser" : "Meta pixel script blocked in this browser — server events still cover it" });
  return out;
}

function Icon({ ok, level }) {
  if (ok === true) return <CheckCircle size={15} weight="fill" style={{ color: COLORS.green }} />;
  if (ok === false) return level === "error" ? <XCircle size={15} weight="fill" style={{ color: COLORS.red }} /> : <Warning size={15} weight="fill" style={{ color: COLORS.yellow }} />;
  return <Info size={15} className="text-neutral-500" />;
}

export default function PixelHealthCard() {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => client.get("/admin/pixels/health").then((r) => setH(r.data)).catch(() => setH({ status: "red", checks: [{ key: "api", ok: false, level: "error", label: "Couldn't load pixel health" }] }));
  useEffect(() => { load(); }, []);
  if (!h) return null;
  const checks = [...(h.checks || []), ...browserChecks(h.pixel_id)];
  const status = checks.some((c) => c.ok === false && c.level === "error") ? "red" : checks.some((c) => c.ok === false) ? "yellow" : "green";
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/admin/pixels/test-event");
      toast.success(`Meta accepted the test event (events_received: ${data.events_received})`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Meta rejected the test event"); load(); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-surface p-5" data-testid="pixel-health-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500"><Pulse size={13} /> Meta pixel health</div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-neutral-300 hover:bg-white/5 inline-flex items-center gap-1.5" data-testid="pixel-health-refresh"><ArrowClockwise size={13} /> Re-check</button>
          <button type="button" onClick={test} disabled={busy || !h.capi_configured} title={h.capi_configured ? "" : "Add the Conversions API token first"}
            className="btn-primary rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="pixel-health-test-event">
            <PaperPlaneTilt size={13} /> Send test event
          </button>
        </div>
      </div>
      <div className="flex items-center gap-3 mb-4" data-testid="pixel-health-status" data-status={status}>
        <span className="inline-block h-3.5 w-3.5 rounded-full" style={{ background: COLORS[status], boxShadow: `0 0 0 4px ${COLORS[status]}22` }} />
        <div>
          <div className="text-lg font-semibold text-white leading-tight">{WORDS[status]}</div>
          <div className="text-[11px] text-neutral-500">Pixel <span className="font-mono text-neutral-300" data-testid="pixel-health-id">{h.pixel_id}</span> · from {h.source === "env" ? "server env" : h.source === "admin" ? "admin setting" : "built-in default"}</div>
        </div>
      </div>
      <ul className="space-y-1.5">
        {checks.map((c) => (
          <li key={c.key} className="flex items-start gap-2 text-xs" data-testid={`pixel-check-${c.key}`}>
            <span className="mt-0.5"><Icon ok={c.ok} level={c.level} /></span>
            <span className={c.ok === false ? "text-white" : "text-neutral-400"}>{c.label}{c.detail ? <span className="text-neutral-600 font-mono"> · {String(c.detail).replace("T", " ").slice(0, 19)}</span> : null}</span>
          </li>
        ))}
      </ul>
      {status !== "green" && (
        <p className="text-[11px] text-neutral-500 mt-3">Red = ads are measuring nothing. Fix the ID under Partner Program → Ad pixels (never the Ad Account number: {(h.ad_account_ids || []).join(", ")}).</p>
      )}
    </div>
  );
}
